import { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ArrowLeft, X, PencilSimple } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

export interface SurveyCompletePayload {
  result: any;
  previousSurvey: ExistingSurvey | null;
  isUpdate: boolean;
}

interface NutritionSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onComplete: (payload: SurveyCompletePayload | null) => void;
  lang?: string;
}

type Gender = 'male' | 'female' | 'prefer_not_to_say';
type ExerciseFrequency = 'rarely' | '1-2_days' | '3-4_days' | '5-7_days';
type NutritionGoal = 'weight_loss' | 'weight_gain' | 'maintain' | 'improve_health' | 'other';

export interface ExistingSurvey {
  gender: Gender;
  birthYear: number;
  heightCm: number;
  weightKg: number;
  exerciseFrequency: ExerciseFrequency;
  nutritionGoal: NutritionGoal;
  targetWeightKg: number | null;
  foodPreferences: string | null;
  cuisinePreferences: string[] | null;
  recommendedCalories: number;
}

const CUISINE_OPTIONS = [
  { key: 'turkish', emoji: '🇹🇷' },
  { key: 'mediterranean', emoji: '🫒' },
  { key: 'asian', emoji: '🍜' },
  { key: 'american', emoji: '🍔' },
  { key: 'mexican', emoji: '🌮' },
  { key: 'indian', emoji: '🍛' },
  { key: 'italian', emoji: '🍝' },
];

const TOTAL_STEPS = 9;

// Animations
const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const stepFadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: ${theme.zIndex.modal + 1};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const HeaderButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated);
  border-radius: ${theme.borderRadius.full};
  color: var(--color-text);
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover { background: var(--color-border-subtle); }
  &:active { transform: scale(0.95); }
`;

const HeaderTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
`;

const HeaderSpacer = styled.div`
  width: 32px;
`;

const ProgressBarTrack = styled.div`
  height: 4px;
  background: var(--color-border-subtle);
  border-radius: 2px;
  margin-bottom: 28px;
  flex-shrink: 0;
`;

const ProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: var(--color-primary);
  border-radius: 2px;
  width: ${({ $progress }) => $progress}%;
  transition: width 0.3s ease;
`;

const StepContent = styled.div`
  animation: ${stepFadeIn} 0.2s ease-out;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 24px 0;
  text-align: center;
`;

const OptionButton = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${({ $selected }) => ($selected ? '#C19FEC' : 'var(--color-text)')};
  color: ${({ $selected }) => ($selected ? '#500FA2' : 'var(--color-bg)')};
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'transparent')};
  border-radius: 12px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 10px;

  &:hover { background: ${({ $selected }) => ($selected ? '#C19FEC' : 'var(--color-text)')}; }
  &:active { transform: scale(0.98); }
`;

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 16px 0;
`;

const SliderValue = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 48px;
  font-weight: 700;
  color: var(--color-text);
`;

const SliderUnit = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin-left: 4px;
`;

const StyledSlider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--color-border);
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    border: 3px solid var(--color-bg);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    border: 3px solid var(--color-bg);
  }
`;

const DualSliderRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const DualSliderColumn = styled.div<{ $flex?: number }>`
  flex: ${({ $flex }) => $flex ?? 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const DualSliderLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SubSlider = styled(StyledSlider)`
  &::-webkit-slider-thumb {
    width: 22px;
    height: 22px;
  }
  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
  }
`;

const YearPickerContainer = styled.div`
  height: 200px;
  overflow-y: auto;
  border-radius: 12px;
  background: var(--color-bg-elevated);
  padding: 8px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;
  }
`;

const YearItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 12px;
  background: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? '#fff' : 'var(--color-text)')};
  border: none;
  border-radius: 8px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: ${({ $selected }) => ($selected ? '600' : '400')};
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;

  &:hover { background: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-bg-elevated)')}; }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 16px;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: var(--color-text-muted); }
  &:focus { border-color: var(--color-primary); }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Chip = styled.button<{ $selected: boolean }>`
  padding: 12px 18px;
  background: ${({ $selected }) => ($selected ? '#C19FEC' : 'var(--color-bg-elevated)')};
  color: ${({ $selected }) => ($selected ? '#500FA2' : 'var(--color-text)')};
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border)')};
  border-radius: 100px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? '600' : '400')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: ${({ $selected }) => ($selected ? '#C19FEC' : 'var(--color-border-subtle)')}; }
  &:active { transform: scale(0.95); }
`;

const ActionButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${({ $disabled }) => ($disabled ? 'rgba(131, 76, 201, 0.4)' : 'var(--color-primary)')};
  color: #fff;
  border: none;
  border-radius: 16px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  margin-top: auto;
  flex-shrink: 0;

  &:hover:not([disabled]) { background: #7040B0; }
  &:active:not([disabled]) { transform: scale(0.98); }
`;

// Summary view styled components
const SummaryContainer = styled.div`
  animation: ${stepFadeIn} 0.2s ease-out;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: var(--color-bg-elevated);
  border-radius: 12px;
`;

const SummaryLabel = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const SummaryValue = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
`;

const EditAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: 4px;

  &:hover { border-color: var(--color-border); color: var(--color-text); }
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: var(--color-bg-elevated);
  margin: 4px 0;
`;

const SummarySectionTitle = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

// Helper maps for display labels
const GENDER_KEYS: Record<Gender, string> = {
  male: 'nutritionSurvey.male',
  female: 'nutritionSurvey.female',
  prefer_not_to_say: 'nutritionSurvey.preferNotToSay',
};
const EXERCISE_KEYS: Record<ExerciseFrequency, string> = {
  rarely: 'nutritionSurvey.rarely',
  '1-2_days': 'nutritionSurvey.1-2days',
  '3-4_days': 'nutritionSurvey.3-4days',
  '5-7_days': 'nutritionSurvey.5-7days',
};
const GOAL_KEYS: Record<NutritionGoal, string> = {
  weight_loss: 'nutritionSurvey.weightLoss',
  weight_gain: 'nutritionSurvey.weightGain',
  maintain: 'nutritionSurvey.maintain',
  improve_health: 'nutritionSurvey.improveHealth',
  other: 'nutritionSurvey.other',
};

export function NutritionSurveyModal({ isOpen, onClose, userId, onComplete }: NutritionSurveyModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  // 'new' = fresh survey, 'summary' = returning user summary view, 'steps' = full wizard
  const [mode, setMode] = useState<'new' | 'summary' | 'steps'>('new');
  const [existingSurvey, setExistingSurvey] = useState<ExistingSurvey | null>(null);

  // Survey data
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthYear, setBirthYear] = useState<number>(1990);
  const [heightCm, setHeightCm] = useState<number>(170);
  const [weightKg, setWeightKg] = useState<number>(70);
  const [exerciseFrequency, setExerciseFrequency] = useState<ExerciseFrequency | null>(null);
  const [nutritionGoal, setNutritionGoal] = useState<NutritionGoal | null>(null);
  const [targetWeightKg, setTargetWeightKg] = useState<number>(65);
  const [foodPreferences, setFoodPreferences] = useState('');
  const [cuisinePreferences, setCuisinePreferences] = useState<string[]>([]);

  const yearPickerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const advanceTimeoutRef = useRef<number | null>(null);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    sheetRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  // Clear any pending auto-advance timeout on unmount
  useEffect(() => {
    return () => {
      if (advanceTimeoutRef.current !== null) {
        window.clearTimeout(advanceTimeoutRef.current);
        advanceTimeoutRef.current = null;
      }
    };
  }, []);

  const populateFromSurvey = useCallback((survey: ExistingSurvey) => {
    setGender(survey.gender);
    setBirthYear(survey.birthYear);
    setHeightCm(survey.heightCm);
    setWeightKg(survey.weightKg);
    setExerciseFrequency(survey.exerciseFrequency);
    setNutritionGoal(survey.nutritionGoal);
    setTargetWeightKg(survey.targetWeightKg ?? 65);
    setFoodPreferences(survey.foodPreferences ?? '');
    setCuisinePreferences(survey.cuisinePreferences ?? []);
  }, []);

  // Fetch existing survey on open
  useEffect(() => {
    if (!isOpen || !userId) return;

    let cancelled = false;

    setStep(0);
    setSubmitting(false);
    setLoading(true);

    const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
    fetch(`${apiUrl}/chat/nutrition-survey/${userId}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && data.data.hasSurvey && data.data.survey) {
          const s = data.data.survey;
          setExistingSurvey(s);
          populateFromSurvey(s);
          setMode('summary');
        } else {
          setExistingSurvey(null);
          setMode('new');
          // Reset to defaults for fresh survey
          setGender(null);
          setBirthYear(1990);
          setHeightCm(170);
          setWeightKg(70);
          setExerciseFrequency(null);
          setNutritionGoal(null);
          setTargetWeightKg(65);
          setFoodPreferences('');
          setCuisinePreferences([]);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        // Fall back to a fresh survey if the existing one can't be loaded
        console.error('[NutritionSurvey] Error fetching existing survey:', error);
        setExistingSurvey(null);
        setMode('new');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, populateFromSurvey]);

  // Scroll to selected year when year picker step is shown
  useEffect(() => {
    if (step === 1 && yearPickerRef.current) {
      const selectedEl = yearPickerRef.current.querySelector('[data-selected="true"]');
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center' });
      }
    }
  }, [step]);

  // Determine effective total steps (skip target weight if goal is maintain/improve_health)
  const shouldSkipTargetWeight = nutritionGoal === 'maintain' || nutritionGoal === 'improve_health';

  const getEffectiveStep = useCallback((s: number): number => {
    if (shouldSkipTargetWeight && s >= 6) return s + 1;
    return s;
  }, [shouldSkipTargetWeight]);

  const effectiveStep = mode === 'new' || mode === 'steps' ? getEffectiveStep(step) : -1;
  const effectiveTotalSteps = shouldSkipTargetWeight ? TOTAL_STEPS - 1 : TOTAL_STEPS;
  const progress = mode === 'summary' ? 100 : ((step + 1) / effectiveTotalSteps) * 100;

  const goNext = useCallback(() => {
    const nextStep = step + 1;
    if (step === 5 && shouldSkipTargetWeight) {
      setStep(7);
      return;
    }
    if (nextStep < TOTAL_STEPS) {
      setStep(nextStep);
    }
  }, [step, shouldSkipTargetWeight]);

  const goBack = useCallback(() => {
    if (step === 7 && shouldSkipTargetWeight) {
      setStep(5);
      return;
    }
    if (step > 0) setStep(step - 1);
  }, [step, shouldSkipTargetWeight]);

  const handleAutoAdvance = useCallback((setter: () => void) => {
    setter();
    if (advanceTimeoutRef.current !== null) {
      window.clearTimeout(advanceTimeoutRef.current);
    }
    advanceTimeoutRef.current = window.setTimeout(() => {
      advanceTimeoutRef.current = null;
      goNext();
    }, 300);
  }, [goNext]);

  const toggleCuisine = (cuisine: string) => {
    setCuisinePreferences(prev =>
      prev.includes(cuisine) ? prev.filter(c => c !== cuisine) : [...prev, cuisine]
    );
  };

  const handleComplete = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/chat/nutrition-survey/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gender,
          birthYear,
          heightCm,
          weightKg,
          exerciseFrequency,
          nutritionGoal,
          targetWeightKg: shouldSkipTargetWeight ? null : targetWeightKg,
          foodPreferences: foodPreferences || null,
          cuisinePreferences: cuisinePreferences.length > 0 ? cuisinePreferences : null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        onComplete({
          result: data.data,
          previousSurvey: existingSurvey,
          isUpdate: !!existingSurvey,
        });
      } else {
        console.error('[NutritionSurvey] API error:', data.error);
        onComplete(null);
      }
    } catch (error) {
      console.error('[NutritionSurvey] Error saving survey:', error);
      onComplete(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAll = () => {
    // Switch to full wizard with existing data pre-filled
    setMode('steps');
    setStep(0);
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = currentYear - 10; y >= currentYear - 80; y--) {
      years.push(y);
    }
    return years;
  };

  if (!isOpen) return null;

  const renderSummaryView = () => {
    if (!existingSurvey) return null;

    return (
      <SummaryContainer>
        <StepTitle>{t('nutritionSurvey.summaryTitle')}</StepTitle>

        <SummaryRow>
          <SummaryLabel>{t('nutritionSurvey.genderTitle').replace('?', '')}</SummaryLabel>
          <SummaryValue>{t(GENDER_KEYS[gender!])}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>{t('nutritionSurvey.birthYearTitle').replace('?', '')}</SummaryLabel>
          <SummaryValue>{birthYear}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>{t('nutritionSurvey.heightTitle').replace('?', '')}</SummaryLabel>
          <SummaryValue>{heightCm} {t('nutritionSurvey.cm')}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>{t('nutritionSurvey.exerciseTitle').replace('?', '')}</SummaryLabel>
          <SummaryValue>{t(EXERCISE_KEYS[exerciseFrequency!])}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>{t('nutritionSurvey.goalTitle').replace('?', '')}</SummaryLabel>
          <SummaryValue>{t(GOAL_KEYS[nutritionGoal!])}</SummaryValue>
        </SummaryRow>

        <EditAllButton onClick={handleEditAll}>
          <PencilSimple size={14} />
          {t('nutritionSurvey.editAll')}
        </EditAllButton>

        <SummaryDivider />
        <SummarySectionTitle>{t('nutritionSurvey.updateSection')}</SummarySectionTitle>

        {/* Editable: Current Weight */}
        <SliderContainer>
          <SummaryLabel>{t('nutritionSurvey.weightTitle')}</SummaryLabel>
          <SliderValue>
            {weightKg.toFixed(1)}
            <SliderUnit>{t('nutritionSurvey.kg')}</SliderUnit>
          </SliderValue>
          <DualSliderRow>
            <DualSliderColumn $flex={3}>
              <DualSliderLabel>{t('nutritionSurvey.kg')}</DualSliderLabel>
              <StyledSlider
                type="range"
                min={30}
                max={200}
                step={1}
                value={Math.floor(weightKg)}
                onChange={e => setWeightKg(Number(e.target.value) + (weightKg % 1))}
              />
            </DualSliderColumn>
            <DualSliderColumn $flex={1}>
              <DualSliderLabel>.{Math.round((weightKg % 1) * 10)}</DualSliderLabel>
              <SubSlider
                type="range"
                min={0}
                max={9}
                step={1}
                value={Math.round((weightKg % 1) * 10)}
                onChange={e => setWeightKg(Math.floor(weightKg) + Number(e.target.value) / 10)}
              />
            </DualSliderColumn>
          </DualSliderRow>
        </SliderContainer>

        {/* Editable: Target Weight (only if relevant goal) */}
        {!shouldSkipTargetWeight && (
          <SliderContainer>
            <SummaryLabel>{t('nutritionSurvey.targetWeightTitle')}</SummaryLabel>
            <SliderValue>
              {targetWeightKg.toFixed(1)}
              <SliderUnit>{t('nutritionSurvey.kg')}</SliderUnit>
            </SliderValue>
            <DualSliderRow>
              <DualSliderColumn $flex={3}>
                <DualSliderLabel>{t('nutritionSurvey.kg')}</DualSliderLabel>
                <StyledSlider
                  type="range"
                  min={30}
                  max={200}
                  step={1}
                  value={Math.floor(targetWeightKg)}
                  onChange={e => setTargetWeightKg(Number(e.target.value) + (targetWeightKg % 1))}
                />
              </DualSliderColumn>
              <DualSliderColumn $flex={1}>
                <DualSliderLabel>.{Math.round((targetWeightKg % 1) * 10)}</DualSliderLabel>
                <SubSlider
                  type="range"
                  min={0}
                  max={9}
                  step={1}
                  value={Math.round((targetWeightKg % 1) * 10)}
                  onChange={e => setTargetWeightKg(Math.floor(targetWeightKg) + Number(e.target.value) / 10)}
                />
              </DualSliderColumn>
            </DualSliderRow>
          </SliderContainer>
        )}

        <ActionButton
          onClick={handleComplete}
          $disabled={submitting}
          style={{ marginTop: '8px' }}
        >
          {submitting ? '...' : t('nutritionSurvey.updateAndContinue')}
        </ActionButton>
      </SummaryContainer>
    );
  };

  const renderStepContent = () => {
    switch (effectiveStep) {
      case 0: // Gender
        return (
          <StepContent key="gender">
            <StepTitle>{t('nutritionSurvey.genderTitle')}</StepTitle>
            {([
              { value: 'male' as Gender, label: t('nutritionSurvey.male') },
              { value: 'female' as Gender, label: t('nutritionSurvey.female') },
              { value: 'prefer_not_to_say' as Gender, label: t('nutritionSurvey.preferNotToSay') },
            ]).map(opt => (
              <OptionButton
                key={opt.value}
                $selected={gender === opt.value}
                onClick={() => handleAutoAdvance(() => setGender(opt.value))}
              >
                {opt.label}
              </OptionButton>
            ))}
          </StepContent>
        );

      case 1: // Birth Year
        return (
          <StepContent key="birthYear">
            <StepTitle>{t('nutritionSurvey.birthYearTitle')}</StepTitle>
            <YearPickerContainer ref={yearPickerRef}>
              {generateYears().map(year => (
                <YearItem
                  key={year}
                  $selected={birthYear === year}
                  data-selected={birthYear === year}
                  onClick={() => setBirthYear(year)}
                >
                  {year}
                </YearItem>
              ))}
            </YearPickerContainer>
            <ActionButton onClick={goNext} style={{ marginTop: '16px' }}>
              {t('nutritionSurvey.next')}
            </ActionButton>
          </StepContent>
        );

      case 2: // Height
        return (
          <StepContent key="height">
            <StepTitle>{t('nutritionSurvey.heightTitle')}</StepTitle>
            <SliderContainer>
              <SliderValue>
                {heightCm}
                <SliderUnit>{t('nutritionSurvey.cm')}</SliderUnit>
              </SliderValue>
              <StyledSlider
                type="range"
                min={100}
                max={220}
                step={1}
                value={heightCm}
                onChange={e => setHeightCm(Number(e.target.value))}
              />
            </SliderContainer>
            <ActionButton onClick={goNext}>
              {t('nutritionSurvey.next')}
            </ActionButton>
          </StepContent>
        );

      case 3: // Weight
        return (
          <StepContent key="weight">
            <StepTitle>{t('nutritionSurvey.weightTitle')}</StepTitle>
            <SliderContainer>
              <SliderValue>
                {weightKg.toFixed(1)}
                <SliderUnit>{t('nutritionSurvey.kg')}</SliderUnit>
              </SliderValue>
              <DualSliderRow>
                <DualSliderColumn $flex={3}>
                  <DualSliderLabel>{t('nutritionSurvey.kg')}</DualSliderLabel>
                  <StyledSlider
                    type="range"
                    min={30}
                    max={200}
                    step={1}
                    value={Math.floor(weightKg)}
                    onChange={e => setWeightKg(Number(e.target.value) + (weightKg % 1))}
                  />
                </DualSliderColumn>
                <DualSliderColumn $flex={1}>
                  <DualSliderLabel>.{Math.round((weightKg % 1) * 10)}</DualSliderLabel>
                  <SubSlider
                    type="range"
                    min={0}
                    max={9}
                    step={1}
                    value={Math.round((weightKg % 1) * 10)}
                    onChange={e => setWeightKg(Math.floor(weightKg) + Number(e.target.value) / 10)}
                  />
                </DualSliderColumn>
              </DualSliderRow>
            </SliderContainer>
            <ActionButton onClick={goNext}>
              {t('nutritionSurvey.next')}
            </ActionButton>
          </StepContent>
        );

      case 4: // Exercise Frequency
        return (
          <StepContent key="exercise">
            <StepTitle>{t('nutritionSurvey.exerciseTitle')}</StepTitle>
            {([
              { value: 'rarely' as ExerciseFrequency, label: t('nutritionSurvey.rarely') },
              { value: '1-2_days' as ExerciseFrequency, label: t('nutritionSurvey.1-2days') },
              { value: '3-4_days' as ExerciseFrequency, label: t('nutritionSurvey.3-4days') },
              { value: '5-7_days' as ExerciseFrequency, label: t('nutritionSurvey.5-7days') },
            ]).map(opt => (
              <OptionButton
                key={opt.value}
                $selected={exerciseFrequency === opt.value}
                onClick={() => handleAutoAdvance(() => setExerciseFrequency(opt.value))}
              >
                {opt.label}
              </OptionButton>
            ))}
          </StepContent>
        );

      case 5: // Nutrition Goal
        return (
          <StepContent key="goal">
            <StepTitle>{t('nutritionSurvey.goalTitle')}</StepTitle>
            {([
              { value: 'weight_loss' as NutritionGoal, label: t('nutritionSurvey.weightLoss') },
              { value: 'weight_gain' as NutritionGoal, label: t('nutritionSurvey.weightGain') },
              { value: 'maintain' as NutritionGoal, label: t('nutritionSurvey.maintain') },
              { value: 'improve_health' as NutritionGoal, label: t('nutritionSurvey.improveHealth') },
              { value: 'other' as NutritionGoal, label: t('nutritionSurvey.other') },
            ]).map(opt => (
              <OptionButton
                key={opt.value}
                $selected={nutritionGoal === opt.value}
                onClick={() => handleAutoAdvance(() => setNutritionGoal(opt.value))}
              >
                {opt.label}
              </OptionButton>
            ))}
          </StepContent>
        );

      case 6: // Target Weight
        return (
          <StepContent key="targetWeight">
            <StepTitle>{t('nutritionSurvey.targetWeightTitle')}</StepTitle>
            <SliderContainer>
              <SliderValue>
                {targetWeightKg.toFixed(1)}
                <SliderUnit>{t('nutritionSurvey.kg')}</SliderUnit>
              </SliderValue>
              <DualSliderRow>
                <DualSliderColumn $flex={3}>
                  <DualSliderLabel>{t('nutritionSurvey.kg')}</DualSliderLabel>
                  <StyledSlider
                    type="range"
                    min={30}
                    max={200}
                    step={1}
                    value={Math.floor(targetWeightKg)}
                    onChange={e => setTargetWeightKg(Number(e.target.value) + (targetWeightKg % 1))}
                  />
                </DualSliderColumn>
                <DualSliderColumn $flex={1}>
                  <DualSliderLabel>.{Math.round((targetWeightKg % 1) * 10)}</DualSliderLabel>
                  <SubSlider
                    type="range"
                    min={0}
                    max={9}
                    step={1}
                    value={Math.round((targetWeightKg % 1) * 10)}
                    onChange={e => setTargetWeightKg(Math.floor(targetWeightKg) + Number(e.target.value) / 10)}
                  />
                </DualSliderColumn>
              </DualSliderRow>
            </SliderContainer>
            <ActionButton onClick={goNext}>
              {t('nutritionSurvey.next')}
            </ActionButton>
          </StepContent>
        );

      case 7: // Food Preferences
        return (
          <StepContent key="foodPrefs">
            <StepTitle>{t('nutritionSurvey.foodPreferencesTitle')}</StepTitle>
            <TextArea
              value={foodPreferences}
              onChange={e => setFoodPreferences(e.target.value)}
              placeholder={t('nutritionSurvey.foodPreferencesPlaceholder')}
            />
            <ActionButton onClick={goNext} style={{ marginTop: '16px' }}>
              {t('nutritionSurvey.next')}
            </ActionButton>
          </StepContent>
        );

      case 8: // Cuisine Preferences
        return (
          <StepContent key="cuisinePrefs">
            <StepTitle>{t('nutritionSurvey.cuisineTitle')}</StepTitle>
            <ChipsContainer>
              {CUISINE_OPTIONS.map(opt => (
                <Chip
                  key={opt.key}
                  $selected={cuisinePreferences.includes(opt.key)}
                  onClick={() => toggleCuisine(opt.key)}
                >
                  {opt.emoji} {t(`nutritionSurvey.${opt.key}`)}
                </Chip>
              ))}
            </ChipsContainer>
            <ActionButton
              onClick={handleComplete}
              $disabled={submitting}
              style={{ marginTop: '24px' }}
            >
              {submitting ? '...' : t('nutritionSurvey.complete')}
            </ActionButton>
          </StepContent>
        );

      default:
        return null;
    }
  };

  const isStepMode = mode === 'new' || mode === 'steps';

  const handleBackOrClose = () => {
    if (isStepMode && step > 0) {
      goBack();
    } else if (mode === 'steps' && step === 0) {
      // Go back to summary from edit-all
      setMode('summary');
      if (existingSurvey) populateFromSurvey(existingSurvey);
    } else {
      onClose();
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Sheet
        ref={sheetRef}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nutrition-survey-title"
        tabIndex={-1}
      >
        <Header>
          {(isStepMode && step > 0) || (mode === 'steps' && step === 0) ? (
            <HeaderButton onClick={handleBackOrClose} aria-label="Back">
              <ArrowLeft size={18} weight="bold" />
            </HeaderButton>
          ) : (
            <HeaderButton onClick={onClose} aria-label="Close">
              <X size={18} weight="bold" />
            </HeaderButton>
          )}
          <HeaderTitle id="nutrition-survey-title">{t('nutritionSurvey.title')}</HeaderTitle>
          <HeaderSpacer />
        </Header>

        {!loading && (
          <ProgressBarTrack>
            <ProgressBarFill $progress={progress} />
          </ProgressBarTrack>
        )}

        {loading ? (
          <StepContent>
            <StepTitle style={{ color: 'var(--color-text-secondary)', fontSize: 16 }}>...</StepTitle>
          </StepContent>
        ) : mode === 'summary' ? (
          renderSummaryView()
        ) : (
          renderStepContent()
        )}
      </Sheet>
    </Overlay>
  );
}
